/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getFullName, getUserId } from "models/User";
import capitalize from "./capitalize";
import isObject from "./isObject";
import { v4 as uuidv4 } from "uuid";
import { removeTrailingNumber } from "components/uploaders/CSVUploader/utils.js";

export const formatArrayToSelectOptions = (array) => {
  const formattedList = array?.map((item) => ({
    label: removeTrailingNumber(item),
    value: item,
    id: uuidv4(),
  }));

  return formattedList;
};

export const formatUsersSelectOptions = (
  options,
  selected = false,
  fieldsToAdd = null,
  setterFunction = null,
) => {
  if (!Array.isArray(options)) return [];
  const formattedOptions = [];
  options.forEach((user) => {
    if (!user) return;
    const format = formatUserObjectSelectOption(
      user,
      selected,
      fieldsToAdd,
      setterFunction,
    );

    formattedOptions.push(format);
  });

  return formattedOptions;
};

export const formatUserObjectSelectOption = (
  option,
  selected = false,
  fieldsToAdd,
  setterFunction,
) => {
  if (!option) return null;
  const opt = {
    name: getFullName(option) || option?.title,
    id: getUserId(option),
    img: option.avatar || option.img || option.icon || option.image,
    avatar: option.avatar || option.img || option.icon || option.image, // for later use
    selected,
    hash_id: option.hash_id,
  };

  if (fieldsToAdd && Array.isArray(fieldsToAdd) && fieldsToAdd.length > 0) {
    fieldsToAdd.forEach((field) => {
      let val;
      if (!setterFunction) {
        val = field.value;
      } else {
        val = setterFunction(option);
      }

      if (isObject(val) && val.hasOwnProperty(field.name)) {
        val = val[field.name];
      }

      opt[field.name] = val;
    });
  }

  return opt;
};

// for native select component
const formatSelectOptions = (
  options,
  optionsToExclude,
  isValueById = false,
  setterFunction,
) => {
  if (!options || !Array.isArray(options) || options.length === 0) return;
  const reshapedOptions = options.map((option) => {
    const label =
      (setterFunction && setterFunction(option).label) ||
      option.name ||
      option.title ||
      option.label ||
      option.value ||
      option.first_name + " " + option.last_name ||
      option.first_name;

    const value =
      (setterFunction && setterFunction(option).value) ||
      (isValueById && (option.id || option.hash_id)) ||
      option.name ||
      option.title ||
      option.value ||
      option.label ||
      option.first_name;

    const id =
      (setterFunction && setterFunction(option).id) ||
      option.id ||
      option.hash_id;

    return {
      label: capitalize(label),
      value,
      id,
    };
  });

  // the purpose of this is to remove the options that are already selected.
  if (Array.isArray(optionsToExclude) && optionsToExclude.length > 0) {
    return removeSelectsFromOptions(reshapedOptions, optionsToExclude);
  }
  // if optionsToExclude is not provided, return the reshaped options.
  return reshapedOptions;
};

const removeSelectsFromOptions = (options, optionsToExclude) => {
  const filteredOptions = options.filter(
    (group) =>
      !optionsToExclude.find(
        (g) =>
          g.value === group.value ||
          g.label === group.label ||
          g.id === group.id,
      ),
  );

  return filteredOptions;
};

export default formatSelectOptions;
